import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from '../../api.js';
import { useAuth } from '../../auth.jsx';
import Alert from '../../components/ui/Alert.jsx';
import ConfirmModal from '../../components/ui/ConfirmModal.jsx';
import { Facts, Loading, PageHeader, Pill, Section } from '../../components/ui/index.jsx';
import { fmtDateTime } from '../../utils/format.js';
import { OutboxStatus, ViaText } from './outboxShared.jsx';

const authPill = (v) => (v === 'pass' ? <Pill tone="good">Pass</Pill> : v ? <Pill tone="warn">{v}</Pill> : '—');

/** One of your own emails we saw: whether it's on record, and who has checked a copy. */
export default function OutboxMessage() {
  const { messageId } = useParams();
  const { organisation } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!organisation) return;
    api(`/portal/organisations/${organisation.id}/outbox/${messageId}`).then(setData).catch((err) => setError(err.message));
  }, [organisation, messageId]);

  const back = { to: '/outbox', label: 'Emails you copied to us' };
  if (error) return <div className="page"><PageHeader back={back} title="Email" /><Alert tone="danger">{error}</Alert></div>;
  if (!data) return <div className="page"><Loading /></div>;
  const m = data.message;

  return (
    <div className="page">
      <PageHeader back={back} title={m.subject || '(no subject)'}
        actions={<>
          {m.has_html && <Link className="btn btn-secondary btn-small" to={`/outbox/${m.id}/view`}>See it as it looked</Link>}
          {data.can_delete && <button type="button" className="btn btn-small btn-danger-ghost" title="Temporary, while testing" onClick={() => setDeleting(true)}>Delete</button>}
        </>} />

      {m.status === 'recorded' && (
        <Alert tone="success">
          On record as yours. We proved it came from you, so anyone who forwards us a copy is told it’s genuine — as long as its
          sender, links, phone numbers and bank details are the same as this one’s.
        </Alert>
      )}
      {m.status === 'not_recorded' && <Alert tone="warn"><strong>We couldn’t prove it came from you, so it isn’t on record.</strong> {m.reason}</Alert>}
      {m.status === 'learned' && <Alert tone="info">We learned the email service it came through. It arrived before we kept a record of each email, so copies of it aren’t confirmed — send the next one with learn@ in Cc and it will be.</Alert>}
      {m.status === 'checking' && <Alert tone="info">Still checking it. Refresh in a minute.</Alert>}

      <Section title="The email">
        <Facts items={[
          ['From', m.from_address ? `${m.from_name ? `${m.from_name} ` : ''}<${m.from_address}>` : null],
          m.reply_to && ['Reply to', m.reply_to],
          ['To', m.to],
          ['Sent', m.sent_at ? fmtDateTime(m.sent_at) : null],
          ['Received by us', fmtDateTime(m.received_at)],
          ['How we saw it', <ViaText via={m.via} />],
          ['Email service', m.service],
          ['Status', <OutboxStatus status={m.status} />],
        ]} />
      </Section>

      <Section title="Proof it came from you" description="What the receiving server checked when it reached us.">
        <Facts items={[
          ['DMARC', authPill(m.auth.dmarc)], ['DKIM', authPill(m.auth.dkim)], ['SPF', authPill(m.auth.spf)],
          m.origin && ['Sent from', `${m.origin.ptr || m.origin.ip}${m.origin.asn ? ` (${m.origin.asn})` : ''}`],
        ]} />
      </Section>

      {(m.links.length > 0 || m.phones.length > 0 || m.emails.length > 0) && (
        <Section title="What’s in it" description="A copy someone forwards must have the same of each, or it isn’t this email.">
          <Facts items={[
            m.links.length > 0 && ['Links to', m.links.join(', ')],
            m.phones.length > 0 && ['Phone numbers', m.phones.join(', ')],
            m.emails.length > 0 && ['Email addresses', m.emails.join(', ')],
          ]} />
        </Section>
      )}

      {data.stamps?.length > 0 && (
        <Section title="Verification stamps" description="The stamps in this email now belong to it. Anyone who opens a stamp sees this email’s sender, date and links; a copy in a different email is caught.">
          <ul className="plain-list">
            {data.stamps.map((x) => (
              <li key={x.code}><span className="mono">{x.code}</span>{x.label && ` · ${x.label}`}{x.revoked ? <span className="muted"> · cancelled</span> : <span className="muted"> · works until {fmtDateTime(x.expires_at)}</span>}</li>
            ))}
          </ul>
        </Section>
      )}

      {m.status === 'recorded' && (
        <Section title="Checked by others" description="People who forwarded us a copy and were told it’s genuine.">
          {data.confirmations.length === 0 ? <p className="muted">Nobody has checked a copy yet.</p> : (
            <ul className="plain-list">
              {data.confirmations.map((c, i) => (
                <li key={i}>{fmtDateTime(c.at)}{c.from_domain && <span className="muted"> · someone at @{c.from_domain}</span>}</li>
              ))}
            </ul>
          )}
        </Section>
      )}

      {m.text_body && (
        <Section title="Text">
          <pre className="email-body">{m.text_body}</pre>
        </Section>
      )}

      {deleting && (
        <ConfirmModal title="Delete this email?" confirmLabel="Delete" danger onClose={() => setDeleting(false)}
          onConfirm={async () => {
            await api(`/portal/organisations/${organisation.id}/outbox/${m.id}`, { method: 'DELETE' });
            navigate('/outbox', { state: { notice: 'Deleted.' } });
          }}>
          <p>Its record goes too, so copies people forward won’t be confirmed by it any more. Temporary, while testing.</p>
        </ConfirmModal>
      )}
    </div>
  );
}
